import { useCallback, useState } from 'react';
import Papa from 'papaparse';

import { validateCsvColumns } from '../utils/validateCsvColumns';
import {
  validateCsvContacts,
  revalidateAfterRemoval,
  type CsvValidationResult,
} from '../utils/validateCsvContacts';

interface UseCsvValidatorReturn {
  validationResult: CsvValidationResult | null;
  isValidating: boolean;
  fileName: string;
  fileSize: number;
  columnWarnings: string[];
  uploadableCount: number;
  validateFile: (file: File) => void;
  removeContact: (rowIndex: number) => void;
  clearValidation: () => void;
}

export default function useCsvValidator (): UseCsvValidatorReturn {
  const [validationResult, setValidationResult] = useState<CsvValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [columnWarnings, setColumnWarnings] = useState<string[]>([]);

  const validateFile = useCallback((file: File): void => {
    setIsValidating(true);
    setFileName(file.name);
    setFileSize(file.size);
    setColumnWarnings([]);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Validate columns
        if (results.meta.fields !== undefined) {
          const colResult = validateCsvColumns(results.meta.fields);
          if (!colResult.valid) {
            setColumnWarnings(colResult.unknownColumns);
          }
        }

        // Validate contacts
        const result = validateCsvContacts(results.data);
        setValidationResult(result);
        setIsValidating(false);
      },
      error: () => {
        setIsValidating(false);
      },
    });
  }, []);

  const removeContact = useCallback((rowIndex: number): void => {
    setValidationResult((prev) => {
      if (prev === null) {
        return null;
      }
      const remaining = prev.contacts.filter((c) => c.rowIndex !== rowIndex);
      return revalidateAfterRemoval(remaining);
    });
  }, []);

  const clearValidation = useCallback((): void => {
    setValidationResult(null);
    setFileName('');
    setFileSize(0);
    setColumnWarnings([]);
  }, []);

  const uploadableCount = validationResult !== null
    ? validationResult.stats.total - validationResult.stats.critical
    : 0;

  return {
    validationResult,
    isValidating,
    fileName,
    fileSize,
    columnWarnings,
    uploadableCount,
    validateFile,
    removeContact,
    clearValidation,
  };
}
